/**
 * Ender dragon fight glue (one instance in main.ts): owns the DragonFight, its
 * renderer and boss bar, routes melee / arrow / explosion damage into it, turns
 * the fight's events into particles and sounds, persists it in the world flags
 * (killed, destroyed crystals, dragon HP) and activates the exit portal (+ egg
 * on the first kill, + the first end gateway when worldgen has one) on death.
 *
 *   enter()/leave() are driven by tick(): call tick() every game tick; it notices
 *   the dimension itself. render(alpha) once a frame.
 */

import * as THREE from 'three';
import { BLOCKS, END_PORTAL_ID } from '../blocks';
import type { ChunkManager } from '../world/manager';
import type { EntityManager } from '../entities';
import type { EntityHost } from '../entities/host';
import { DragonFight } from '../entities/end/fight';
import { EndSpawner } from '../entities/end/spawning';
import { DragonRenderer } from '../render/entities/dragon';
import type { EnvUniforms } from '../render/env';
import type { GuiStage } from '../ui/kit';
import { BossBar } from '../ui/bossBar';
import * as endGen from '../worldgen/end';
import { END_EGG_POS, END_EXIT_PORTAL, END_PILLARS, buildExitPortal } from '../worldgen/end';

export interface EndFightGlueHost {
  chunks: ChunkManager;
  entities: EntityManager;
  env: EnvUniforms;
  scene: THREE.Object3D;
  camera: THREE.Camera;
  stage: GuiStage;
  player: EntityHost['player'];
  save: { flags: Set<string>; setFlag(key: string): void };
  /** Block explosion + damage (entityHost.explode). */
  explode(x: number, y: number, z: number, power: number): void;
  dropXp(amount: number, x: number, y: number, z: number): void;
  particles: {
    explosion(x: number, y: number, z: number, power: number): void;
    breath?(x: number, y: number, z: number): void;
    portal(blockId: number, x: number, y: number, z: number, tx: number, ty: number, tz: number): void;
  };
  sfx: { play(id: string, pos?: { x: number; y: number; z: number }): void };
  /** Damage of a full-strength melee swing with the held item (charge included). */
  meleeDamage(): number;
  /** The swing connected with the dragon / a crystal: cooldown reset, durability, exhaustion. */
  afterMelee(): void;
  tickCount(): number;
  /**
   * Thrown things in flight other than arrows (eggs, tridents): this tick's
   * segment, the damage they would deal, and `hit()` to spend them on impact.
   */
  projectiles?(): Iterable<{ x: number; y: number; z: number; px: number; py: number; pz: number; damage: number; hit(): void }>;
}

const PORTAL_PARTICLE = BLOCKS.findIndex((b) => b?.name === 'Nether Portal');
const FLAG_PREFIX = 'dragon_';

export class EndFightGlue {
  readonly fight: DragonFight;
  private readonly renderer: DragonRenderer;
  private readonly bar: BossBar;
  private readonly spawner: EndSpawner;
  private inEnd = false;
  private lastFlags = '';
  private flagTimer = 0;
  private growls = 0;

  constructor(private readonly h: EndFightGlueHost) {
    const set = (x: number, y: number, z: number, id: number): void => { h.chunks.setBlock(x, y, z, id); };
    this.fight = new DragonFight({
      getBlock: (x, y, z) => h.chunks.world.getBlock(x, y, z),
      breakBlock: (x, y, z) => { if (h.chunks.canEdit(x, y, z)) h.chunks.setBlock(x, y, z, 0); },
      player: h.player,
      explode: (x, y, z, p) => h.explode(x, y, z, p),
      dropXp: (a, x, y, z) => h.dropXp(a, x, y, z),
      effect: (kind, x, y, z) => this.effect(kind, x, y, z),
      activatePortal: (egg) => {
        buildExitPortal(set, true);
        // buildExitPortal always sets the egg; only the first kill leaves one
        if (!egg) set(END_EGG_POS.x, END_EGG_POS.y, END_EGG_POS.z, 0);
        // the first kill opens the first outer-island gateway once worldgen provides it
        if (egg) endGen.buildEndGateway(set, 0);
      },
      deactivatePortal: () => {
        const e = END_EXIT_PORTAL;
        for (let dz = -3; dz <= 3; dz++) for (let dx = -3; dx <= 3; dx++) {
          if (h.chunks.world.getBlock(e.x + dx, e.y, e.z + dz) === END_PORTAL_ID) set(e.x + dx, e.y, e.z + dz, 0);
        }
      },
      changed: () => this.persist(),
      rnd: Math.random,
    }, { pillars: END_PILLARS, exit: END_EXIT_PORTAL });

    this.renderer = new DragonRenderer(h.env);
    this.renderer.group.visible = false;
    h.scene.add(this.renderer.group);
    this.bar = new BossBar(h.stage.content);
    this.spawner = new EndSpawner({
      world: h.chunks.world,
      rnd: Math.random,
      player: () => h.player,
      spawn: (t, x, y, z) => h.entities.spawnMob(t, x, y, z),
      count: () => h.entities.mobs.reduce((n, m) => n + (!m.dying && m.type === 'enderman' ? 1 : 0), 0),
    });
  }

  private effect(kind: string, x: number, y: number, z: number): void {
    const p = this.h.particles, s = this.h.sfx;
    switch (kind) {
      case 'roar': s.play('entity.ender_dragon.growl', { x, y, z }); break;
      case 'hurt': s.play('entity.ender_dragon.hurt', { x, y, z }); break;
      case 'flap': s.play('entity.ender_dragon.flap', { x, y, z }); break;
      case 'growl': {
        // fired every 4 ticks of the death animation: a burst every other one, a dying roar every 2 s
        const n = this.growls++;
        if (n % 10 === 0) s.play('entity.ender_dragon.death', { x, y, z });
        if (n % 2 === 0) p.explosion(x + (Math.random() - 0.5) * 8, y + (Math.random() - 0.5) * 4, z + (Math.random() - 0.5) * 8, 1);
        break;
      }
      case 'death_blast': p.explosion(x, y, z, 6); s.play('entity.generic.explode', { x, y, z }); break;
      case 'fireball_burst': s.play('entity.generic.explode', { x, y, z }); break;
      default: break; // crystal_blast (the TNT sim already plays and smokes the blast)
    }
  }

  /** Explosions elsewhere (TNT) pop crystals: main chains this onto tnt.onExplode. */
  onExplosion(x: number, y: number, z: number, power: number): void {
    if (this.inEnd) this.fight.onExplosion(x, y, z, power);
  }

  /** Glass bottle aimed at the dragon's breath cloud: true when a bottle was filled (the caller hands out Dragon's Breath). */
  takeBreath(eye: THREE.Vector3, dir: THREE.Vector3, reach: number): boolean {
    return this.inEnd && this.fight.takeBreath(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, reach);
  }

  /** Left click: hit a dragon part or crystal if the crosshair meets one before `blockDist`. */
  /** End crystal item used on block (x, y, z): true when a crystal was placed. */
  placeCrystal(x: number, y: number, z: number): boolean {
    return this.h.chunks.dimension === 'end' && this.fight.placeCrystal(x, y, z);
  }

  melee(eye: THREE.Vector3, dir: THREE.Vector3, reach: number, blockDist: number): boolean {
    if (!this.inEnd) return false;
    const r = this.fight.attackRay(eye.x, eye.y, eye.z, dir.x, dir.y, dir.z, Math.min(reach, blockDist), this.h.meleeDamage(), 'player');
    if (!r) return false;
    this.h.afterMelee();
    return true;
  }

  tick(): void {
    const h = this.h;
    const here = h.chunks.dimension === 'end';
    if (here && !this.inEnd) {
      this.inEnd = true;
      this.fight.loadFlags([...h.save.flags].filter((f) => f.startsWith(FLAG_PREFIX)));
      this.fight.enter();
      this.lastFlags = this.fight.saveFlags().sort().join('|');
    } else if (!here && this.inEnd) {
      this.persist();
      this.inEnd = false;
      this.fight.leave();
    }
    if (!here) return;
    this.fight.tick();
    this.spawner.tick();
    this.arrows();
    this.breathParticles();
    // HP moves constantly: write it out at most every 5 s
    if (++this.flagTimer >= 100) { this.flagTimer = 0; this.persist(); }
  }

  /** Arrows fired this tick vs the dragon parts / crystals (deflect off a perched dragon). */
  private arrows(): void {
    for (const a of this.h.entities.arrows) {
      if (a.removed || a.stuck || a.spent) continue;
      const r = this.fight.projectileHit(a.px, a.py, a.pz, a.x, a.y, a.z, a.damage + (a.crit ? 1 : 0));
      if (r === 'deflected') { a.vx *= -0.3; a.vz *= -0.3; a.vy = 0.1; a.spent = true; this.h.sfx.play('entity.generic.hurt', a); }
      else if (r) a.removed = true;
    }
    // eggs and tridents pop crystals and hit the dragon too (and bounce off it while it perches)
    for (const t of this.h.projectiles?.() ?? []) {
      if (this.fight.projectileHit(t.px, t.py, t.pz, t.x, t.y, t.z, t.damage)) t.hit();
    }
  }

  private breathParticles(): void {
    if (this.h.tickCount() % 2 !== 0) return;
    for (const c of this.fight.clouds) {
      const a = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * c.radius;
      const x = c.x + Math.cos(a) * r, z = c.z + Math.sin(a) * r;
      if (this.h.particles.breath) this.h.particles.breath(x, c.y + 0.1, z);
      else if (PORTAL_PARTICLE >= 0) this.h.particles.portal(PORTAL_PARTICLE, x, c.y + 0.1, z, x, c.y + 1.2, z);
    }
  }

  /** Mirror the fight's state into the world flags (only when it changed). */
  persist(): void {
    const want = this.fight.saveFlags();
    const key = want.slice().sort().join('|');
    if (key === this.lastFlags) return;
    this.lastFlags = key;
    for (const f of [...this.h.save.flags]) if (f.startsWith(FLAG_PREFIX)) this.h.save.flags.delete(f);
    for (const f of want) this.h.save.setFlag(f);
  }

  render(alpha: number): void {
    this.renderer.group.visible = this.inEnd;
    if (!this.inEnd) { this.bar.update(null, this.h.stage.width); return; }
    this.renderer.update(this.fight, alpha, this.h.camera, this.h.tickCount());
    this.bar.update(this.fight.bossBar(), this.h.stage.width);
  }
}
